/**
 * File: businessesService.ts
 * Path: apps/admin/src/shared/services/businessesService.ts
 * Description: The one-record-per-business layout (businesses/{id}) as the
 * admin uses it while the move is still reaching records: which collection a
 * record lives in (the business when there is one, else the old lead or
 * client), the list of businesses, and the two duplicate decisions, Merge and
 * Not the same. Takes Firestore from a getter so tests can swap it.
 */

import {
  BUSINESSES_COLLECTION,
  db,
  FirestoreService,
  FunctionsService,
  limit,
  planMerge,
  orderBy,
  type BusinessMergeLine,
  type BusinessMergeResult,
  type BusinessRow,
} from "@idk/config";
import { arrayUnion, doc, getDoc, writeBatch, type Firestore } from "firebase/firestore";
import type { Row } from "../utils/businessRecordUtils";

/** Far above the data today (about 70 records), and the lists cap themselves. */
const BUSINESSES_READ_LIMIT = 1500;

export type OldCollection = "leads" | "clients";

/** Where a record's data lives: its business when one exists, else the old collection. */
export const recordHome = async (db: Firestore, id: string, old: OldCollection): Promise<string> =>
  (await getDoc(doc(db, BUSINESSES_COLLECTION, id))).exists() ? BUSINESSES_COLLECTION : old;

export const businessJobsPath = (id: string): string => `${BUSINESSES_COLLECTION}/${id}/jobs`;

export interface MergePreview {
  keepId: string;
  dropId: string;
  keepName: string;
  dropName: string;
  lines: BusinessMergeLine[];
}

interface MergeInput {
  keepId: string;
  dropId: string;
  apply: boolean;
}

const nameOf = (row: BusinessRow): string => (typeof row.name === "string" ? row.name.trim() : "");

/** Reads both records and picks the older to keep, whichever way round they are asked. */
const plan = async (store: Firestore, aId: string, bId: string) => {
  const [aSnap, bSnap] = await Promise.all([
    getDoc(doc(store, BUSINESSES_COLLECTION, aId)),
    getDoc(doc(store, BUSINESSES_COLLECTION, bId)),
  ]);
  if (!aSnap.exists() || !bSnap.exists()) throw new Error("One of these records isn't there any more.");
  return planMerge({ id: aSnap.id, ...aSnap.data() }, { id: bSnap.id, ...bSnap.data() });
};

export const createBusinessesRepo = (getDb: () => Firestore) => ({
  /** Every business, newest activity first. */
  all: async (): Promise<Row[]> =>
    FirestoreService.query<Row>(BUSINESSES_COLLECTION, orderBy("updatedAt", "desc"), limit(BUSINESSES_READ_LIMIT)),

  /** "Not the same": each record stores the other as checked, so the banner goes from both. */
  markNotDuplicate: async (aId: string, bId: string): Promise<void> => {
    const batch = writeBatch(getDb());
    batch.update(doc(getDb(), BUSINESSES_COLLECTION, aId), { notDuplicateOf: arrayUnion(bId) });
    batch.update(doc(getDb(), BUSINESSES_COLLECTION, bId), { notDuplicateOf: arrayUnion(aId) });
    await batch.commit();
  },

  /**
   * Asks the mergeBusinesses function what a merge would do, without doing
   * it: which record is kept (the older), which is removed (the newer), and
   * how much moves. A refusal (two numbered clients) arrives as the server's
   * plain message.
   */
  previewMerge: async (aId: string, bId: string): Promise<MergePreview> => {
    const { keep, drop } = await plan(getDb(), aId, bId);
    const res = await FunctionsService.call<MergeInput, BusinessMergeResult>("mergeBusinesses", {
      keepId: keep.id,
      dropId: drop.id,
      apply: false,
    });
    if ("error" in res) throw new Error(res.message);
    return {
      keepId: keep.id,
      dropId: drop.id,
      keepName: nameOf(keep),
      dropName: nameOf(drop),
      lines: res.lines,
    };
  },

  /**
   * "Merge": keeps the older of the two and joins the other into it. The work
   * is done by the mergeBusinesses function on the server, which also moves
   * the contracts, quotes, invoices, time, journals and reminders over and
   * refuses two numbered clients. Only called after the preview was confirmed.
   * Returns the id that was kept.
   */
  merge: async (aId: string, bId: string): Promise<string> => {
    const { keep, drop } = await plan(getDb(), aId, bId);
    await FunctionsService.call<MergeInput, unknown>("mergeBusinesses", {
      keepId: keep.id,
      dropId: drop.id,
      apply: true,
    });
    return keep.id;
  },
});

export const businessesRepo = createBusinessesRepo(() => db);

export type BusinessesRepo = ReturnType<typeof createBusinessesRepo>;
